import { AxiosResponse } from "axios";
import fileDownload from 'js-file-download';
import config from "../../../../config/config";
import ApiClient from "../../ApiClient";
import {
    IAttachedEquipment,
    IAttachedMaterial,
    IAttachedWork,
    ICreateElementsPosition, IDeletePositionsResponse,
    IDerivedPosition,
    IElementsPosition,
    IManualPosition,
    IPositionFormConfiguration, IUpdateAttachedEquipmentRequestData,
    IUpdateAttachedMaterialRequestData,
    IUpdateAttachedWorkRequestData, IUpdateElementPositionFull,
    IUpdateManualPositionRequestData,
    IUpdatePositionBasicsRequestData,
    MaterialType,
} from "./types";
import { IGraphics } from "../graphics/types";

const elementsApi = new ApiClient("/Elements", config.api.baseUrl);

/**
 * Returns a list of position, global or project specific.
 * @param token
 */
export const getElementsPositions = (token: string): Promise<IElementsPosition[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get("/positions", {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IElementsPosition[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Returns flat list of positions IDs for specific project.
 * @param token
 * @param projectID
 */
export const getProjectElementsPositions = (token: string, projectID: string): Promise<number[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/positions/${projectID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<number[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Create new elements position.
 * @param token
 * @param data
 */
export const createElementsPosition = (token: string, data: ICreateElementsPosition): Promise<IElementsPosition[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/positions/create`, data).then((response: AxiosResponse<IElementsPosition[]>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Update elements position.
 * @param token
 * @param data
 */
export const updateElementPositionFull = (token: string, data: IUpdateElementPositionFull): Promise<IElementsPosition> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/positions/update`, data).then((response: AxiosResponse<IElementsPosition>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Delete positions from catalogue.
 * @param token
 * @param positionIDs
 */
export const deleteElementsPosition = (token: string, positionIDs: number[]): Promise<IDeletePositionsResponse> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/positions/delete`, positionIDs).then((response: AxiosResponse<IDeletePositionsResponse>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Assign position to project.
 * @param token
 * @param projectID
 * @param positionIDs
 */
export const addProjectElementsPositions = (
    token: string,
    projectID: string,
    positionIDs: number[]
): Promise<number[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/positions/${projectID}/add`, positionIDs).then((response: AxiosResponse<number[]>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Delete position from project.
 * @param token
 * @param projectID
 * @param positionIDs
 */
export const deleteProjectElementsPositions = (
    token: string,
    projectID: string,
    positionIDs: number[]
): Promise<number[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/positions/${projectID}/delete`, positionIDs).then((response: AxiosResponse<number[]>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * This action will detect the master file and latest version.
 * @param token
 * @param projectID
 */
export const extractProjectElements = (token: string, projectID: number): Promise<void> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.get(`/extract/project/${projectID}`).then((response: AxiosResponse<void>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Return list of derived positions.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getDerivedPositions = (
    token: string,
    projectID: number,
    positionID: number
): Promise<IDerivedPosition[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/derived/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IDerivedPosition[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Return list of basic positions.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getBasicPositions = (
    token: string,
    projectID: number,
    positionID: number,
): Promise<IDerivedPosition[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/basic/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IDerivedPosition[]>) => {
            return Promise.resolve(response.data);
        });
};


/**
 * Add element category as derived.
 * @param token
 * @param projectID
 * @param positionID
 * @param positionIDs
 * @param revereseMode
 */
export const addDerivedPositions = (
    token: string,
    projectID: number,
    positionID: number,
    positionIDs: number[],
    reverseMode: boolean,
): Promise<IDerivedPosition[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/derived/${projectID}/${positionID}/add?reverseMode=${reverseMode?'true':'false'}`, positionIDs)
        .then((response: AxiosResponse<IDerivedPosition[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Update derived element category.
 * @param token
 * @param projectID
 * @param positionID
 * @param positionIDs
 */
export const updateDerivedPosition = (
    token: string,
    derivedPositionID: number,
    derivedPosition: IDerivedPosition
): Promise<IDerivedPosition> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/derived/${derivedPositionID}/update`, derivedPosition)
        .then((response: AxiosResponse<IDerivedPosition>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Delete derived positions.
 * @param token
 * @param projectID
 * @param positionID
 * @param derivedPositionIDs
 */
export const deleteDerivedPositions = (
    token: string,
    derivedPositionIDs: number[]
): Promise<boolean> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/derived/delete`, derivedPositionIDs)
        .then((response: AxiosResponse<boolean>) => {
            return Promise.resolve(response.status === 200 || response.status === 204);
        });
};

/**
 * Get attached work.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getAttachedWork = (token: string, projectID: number, positionID: number): Promise<IAttachedWork[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/work/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IAttachedWork[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Get attached graphics.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getAttachedGraphics = (token: string, projectID: number, positionID: number): Promise<IGraphics> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/graphics/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IGraphics>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Delete work attachment.
 * @param token
 * @param projectID
 * @param positionID
 * @param attachedWorkIDs
 */
export const deleteAttachedWork = (
    token: string,
    projectID: number,
    positionID: number,
    attachedWorkIDs: number[]
): Promise<boolean> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/work/${projectID}/${positionID}/delete`, attachedWorkIDs)
        .then((response: AxiosResponse<boolean>) => {
            return Promise.resolve(response.status === 204);
        });
};

/**
 * Add work to position.
 * @param token
 * @param projectID
 * @param positionID
 * @param workIDs
 */
export const addWorkToPosition = (
    token: string,
    projectID: number,
    positionID: number,
    workIDs: number[]
): Promise<IAttachedWork[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/work/${projectID}/${positionID}/add`, workIDs)
        .then((response: AxiosResponse<IAttachedWork[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Add graphics to position.
 * @param token
 * @param projectID
 * @param positionID
 * @param graphicsID
 */
export const addGraphicsToPosition = (
    token: string,
    projectID: number,
    positionID: number,
    graphicsID: number
): Promise<IGraphics> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/graphics/${projectID}/${positionID}/add/${graphicsID}`)
        .then((response: AxiosResponse<IGraphics>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Get attached equipment.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getAttachedEquipment = (token: string, projectID: number, positionID: number): Promise<IAttachedEquipment[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/equipment/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IAttachedEquipment[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Delete equipment attachment.
 * @param token
 * @param projectID
 * @param positionID
 * @param attachedEquipmentIDs
 */
export const deleteAttachedEquipment = (
    token: string,
    projectID: number,
    positionID: number,
    attachedEquipmentIDs: number[]
): Promise<boolean> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/equipment/${projectID}/${positionID}/delete`, attachedEquipmentIDs)
        .then((response: AxiosResponse<boolean>) => {
            return Promise.resolve(response.status === 204);
        });
};

/**
 * Add equipment to position.
 * @param token
 * @param projectID
 * @param positionID
 * @param equipmentIDs
 */
export const addEquipmentToPosition = (
    token: string,
    projectID: number,
    positionID: number,
    equipmentIDs: number[]
): Promise<IAttachedEquipment[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/equipment/${projectID}/${positionID}/add`, equipmentIDs)
        .then((response: AxiosResponse<IAttachedEquipment[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Update attached equipment and return updated entity.
 * @param token
 * @param attachedEquipmentID
 * @param data
 */
export const updateAttachedEquipment = (
    token: string,
    attachedEquipmentID: number,
    data: IUpdateAttachedEquipmentRequestData
): Promise<IAttachedEquipment> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/equipment/${attachedEquipmentID}/update`, data).then((response: AxiosResponse<IAttachedEquipment>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Get attached material.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getAttachedMaterial = (
    token: string,
    projectID: number,
    positionID: number
): Promise<IAttachedMaterial[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/material/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<IAttachedMaterial[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Delete material attachment.
 * @param token
 * @param projectID
 * @param positionID
 * @param attachedMaterialIDs
 */
export const deleteAttachedMaterial = (
    token: string,
    projectID: number,
    positionID: number,
    attachedMaterialIDs: number[]
): Promise<boolean> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/material/${projectID}/${positionID}/delete`, attachedMaterialIDs)
        .then((response: AxiosResponse<boolean>) => {
            return Promise.resolve(response.status === 204);
        });
};

/**
 * Add material to position.
 * @param token
 * @param projectID
 * @param positionID
 * @param materialType
 * @param materialIDs
 */
export const addMaterialToPosition = (
    token: string,
    projectID: number,
    positionID: number,
    materialType: MaterialType,
    materialIDs: number[]
): Promise<IAttachedMaterial[]> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/material/${projectID}/${positionID}/add/${materialType}`, materialIDs)
        .then((response: AxiosResponse<IAttachedMaterial[]>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Get unit price for position.
 * @param token
 * @param projectID
 * @param positionID
 */
export const getUnitPrice = (token: string, projectID: number, positionID: number): Promise<number> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/unitprice/${projectID}/${positionID}`, {
            clearCacheEntry: true,
        })
        .then((response: AxiosResponse<number>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Update attached material and return updated entity.
 * @param token
 * @param attachedMaterialID
 * @param data
 */
export const updateAttachedMaterial = (
    token: string,
    attachedMaterialID: number,
    data: IUpdateAttachedMaterialRequestData
): Promise<IAttachedMaterial> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/material/${attachedMaterialID}/update`, data)
        .then((response: AxiosResponse<IAttachedMaterial>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Update attached work and return updated entity.
 * @param token
 * @param attachedWorkID
 * @param data
 */
export const updateAttachedWork = (
    token: string,
    attachedWorkID: number,
    data: IUpdateAttachedWorkRequestData
): Promise<IAttachedWork> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.post(`/work/${attachedWorkID}/update`, data).then((response: AxiosResponse<IAttachedWork>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Update position basics - returns updated position.
 * @param token
 * @param projectID
 * @param positionID
 * @param data
 */
export const updatePositionBasics = (
    token: string,
    projectID: number,
    positionID: number,
    data: IUpdatePositionBasicsRequestData
): Promise<IElementsPosition> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/positions/${projectID}/${positionID}/update`, data)
        .then((response: AxiosResponse<IElementsPosition>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Update manual position - returns updated manual position data.
 * @param token
 * @param projectID
 * @param positionID
 * @param data
 */
export const updateManualPosition = (
    token: string,
    projectID: number,
    positionID: number,
    data: IUpdateManualPositionRequestData
): Promise<IManualPosition> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/positions/${projectID}/${positionID}/manual`, data)
        .then((response: AxiosResponse<IManualPosition>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Get manual position - returns manual position data.
 * @param token
 * @param positionID
 */
export const getManualPosition = (token: string, positionID: number): Promise<IManualPosition> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi.get(`/positions/${positionID}/manual`).then((response: AxiosResponse<IManualPosition>) => {
        return Promise.resolve(response.data);
    });
};

/**
 * Get position form configuration - returns form configuration.
 * @param token
 */
export const getPositionFormConfiguration = (
    token: string
): Promise<IPositionFormConfiguration> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/positions/form`)
        .then((response: AxiosResponse<IPositionFormConfiguration>) => {
            return Promise.resolve(response.data);
        });
};

/**
 * Export positions, file is downloaded
 * @param token
 */
export const exportPositions = (
    token: string
): Promise<void> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .get(`/positions/export`, {
            responseType: "blob"
        })
        .then((response) => {
            // Promise.resolve(response.data)
            fileDownload(response.data, "positions.xlsx");
            return;
        });
};

/**
 * Import positions, file is uploaded
 * @param token
 * @param data
 */
export const importPositions = (
    token: string,
    data: FormData
): Promise<void> => {
    ApiClient.setAuthorizationToken(token);

    return elementsApi
        .post(`/positions/import`, data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {
            return Promise.resolve(response.data);
        })
        .catch((exception) => {
            return Promise.reject(exception.response.data);
        });
};